import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { workerTeamsAPI, userAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Users,
  Crown,
  MapPin,
  Star,
  Briefcase,
  SpinnerGap,
  ArrowLeft,
  PencilSimple,
  Trash,
  UserPlus,
  SignOut,
  Check,
  X,
  MagnifyingGlass,
  CurrencyDollar,
  Eye,
  EyeSlash,
  PaperPlaneTilt
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const WorkerTeamDetails = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [inviteRole, setInviteRole] = useState('');
  const [invitingWorker, setInvitingWorker] = useState(null);
  const [leavingTeam, setLeavingTeam] = useState(false);
  const [deletingTeam, setDeletingTeam] = useState(false);
  const [removingMember, setRemovingMember] = useState(null);

  useEffect(() => {
    fetchTeam();
    fetchCurrentUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [teamId]);

  const fetchTeam = async () => {
    try {
      setLoading(true);
      const response = await workerTeamsAPI.getById(teamId);
      if (response.data.success) {
        setTeam(response.data.team);
      }
    } catch (error) {
      console.error('Error fetching team:', error);
      toast({
        title: 'Error',
        description: 'Failed to load team details',
        variant: 'destructive'
      });
      navigate('/worker-teams');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await userAPI.getMe();
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const handleSearchWorkers = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      setSearchLoading(true);
      const response = await workerTeamsAPI.searchWorkers({ search: searchQuery });
      if (response.data.success) {
        // Filter out workers already in the team
        const teamMemberIds = team.members.map(m => m.user_id);
        const filtered = response.data.workers.filter(w => !teamMemberIds.includes(w.id));
        setSearchResults(filtered);
      }
    } catch (error) {
      console.error('Error searching workers:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleInviteWorker = async (workerId) => {
    try {
      setInvitingWorker(workerId);
      const response = await workerTeamsAPI.inviteWorker(teamId, {
        worker_id: workerId,
        role: inviteRole
      });
      if (response.data.success) {
        toast({
          title: 'Invitation Sent',
          description: response.data.message
        });
        setSearchResults(prev => prev.filter(w => w.id !== workerId));
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send invitation',
        variant: 'destructive'
      });
    } finally {
      setInvitingWorker(null);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member from the team?')) return;
    
    try {
      setRemovingMember(memberId);
      const response = await workerTeamsAPI.removeMember(teamId, memberId);
      if (response.data.success) {
        toast({
          title: 'Member Removed',
          description: 'The member has been removed from the team'
        });
        fetchTeam();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to remove member',
        variant: 'destructive'
      });
    } finally {
      setRemovingMember(null);
    }
  };

  const handleLeaveTeam = async () => {
    if (!window.confirm('Are you sure you want to leave this team?')) return;
    
    try {
      setLeavingTeam(true);
      const response = await workerTeamsAPI.leave(teamId);
      if (response.data.success) {
        toast({
          title: 'Left Team',
          description: response.data.message
        });
        navigate('/worker-teams');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to leave team',
        variant: 'destructive'
      });
    } finally {
      setLeavingTeam(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) return;
    
    try {
      setDeletingTeam(true);
      const response = await workerTeamsAPI.delete(teamId);
      if (response.data.success) {
        toast({
          title: 'Team Deleted',
          description: 'Your team has been deleted'
        });
        navigate('/worker-teams');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete team',
        variant: 'destructive'
      });
    } finally {
      setDeletingTeam(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F0F2] flex items-center justify-center">
        <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen bg-[#F0F0F2] flex items-center justify-center">
        <p className="text-gray-500">Team not found</p>
      </div>
    );
  }

  const isLeader = team.is_leader;
  const isMember = team.is_member;

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="worker-team-details-page">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-8 border-b-4 border-black">
        <div className="max-w-5xl mx-auto px-4">
          <button 
            onClick={() => navigate('/worker-teams')}
            className="flex items-center gap-2 text-blue-100 hover:text-white mb-4 font-bold"
          >
            <ArrowLeft size={20} weight="bold" />
            Back to My Teams
          </button>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl md:text-4xl font-black">{team.name}</h1>
                {team.visibility === 'private' && (
                  <Badge className="bg-white/20 text-white border-white/40">
                    <EyeSlash size={14} className="mr-1" />
                    Private
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-4 text-blue-100">
                <span className="flex items-center gap-1">
                  <Users size={18} weight="bold" />
                  {team.member_count} members
                </span>
                {team.location && (
                  <span className="flex items-center gap-1">
                    <MapPin size={18} weight="bold" />
                    {team.location}
                  </span>
                )}
                {team.rating > 0 && (
                  <span className="flex items-center gap-1 text-amber-300">
                    <Star size={18} weight="fill" />
                    {team.rating.toFixed(1)}
                  </span>
                )}
              </div>
            </div>
            {isLeader && (
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowInviteModal(true)}
                  className="bg-white text-[#2B4C6F] hover:bg-blue-50 font-bold"
                  data-testid="invite-member-btn"
                >
                  <UserPlus size={20} weight="bold" className="mr-2" />
                  Invite
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
              <h2 className="text-xl font-black text-gray-900 mb-4">About the Team</h2>
              <p className="text-gray-600 whitespace-pre-wrap">{team.description}</p>
              
              {team.skills && team.skills.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-2">Team Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {team.skills.map((skill, idx) => (
                      <Badge key={idx} variant="secondary" className="px-3 py-1">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Team Members */}
            <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
              <h2 className="text-xl font-black text-gray-900 mb-4">
                Team Members ({team.members?.length || 0})
              </h2>
              <div className="space-y-4">
                {team.members?.map((member) => (
                  <div 
                    key={member.user_id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border-2 border-gray-200"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                        member.user_id === team.leader_id 
                          ? 'bg-amber-100 border-amber-400' 
                          : 'bg-[#2B4C6F]/10 border-[#2B4C6F]'
                      }`}>
                        {member.user_id === team.leader_id ? (
                          <Crown size={20} weight="fill" className="text-amber-600" />
                        ) : (
                          <Users size={20} weight="bold" className="text-[#2B4C6F]" />
                        )}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{member.name}</p>
                        <p className="text-sm text-gray-500">{member.role || 'Team Member'}</p>
                        {member.skills && member.skills.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {member.skills.slice(0, 3).map((skill, idx) => (
                              <span key={idx} className="text-xs bg-gray-200 px-2 py-0.5 rounded">
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {member.hourly_rate && team.pricing_type === 'individual' && (
                        <span className="text-sm font-bold text-gray-600">
                          ${member.hourly_rate}/hr
                        </span>
                      )}
                      {isLeader && member.user_id !== team.leader_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveMember(member.user_id)}
                          disabled={removingMember === member.user_id}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          {removingMember === member.user_id ? (
                            <SpinnerGap size={16} className="animate-spin" />
                          ) : (
                            <Trash size={16} weight="bold" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Team Applications */}
            {isMember && (
              <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-black text-gray-900">Team Applications</h2>
                  <Button
                    onClick={() => navigate('/browse-jobs')}
                    className="bg-[#2B4C6F]"
                  >
                    <Briefcase size={16} weight="bold" className="mr-2" />
                    Find Jobs
                  </Button>
                </div>
                <p className="text-gray-500 text-sm">
                  {isLeader 
                    ? "As the team leader, you can apply to jobs on behalf of the entire team."
                    : "Only the team leader can submit applications for the team."
                  }
                </p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Team Stats */}
            <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
              <h3 className="font-black text-gray-900 mb-4">Team Info</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Pricing</span>
                  <span className="font-bold text-gray-900">
                    {team.pricing_type === 'team_rate' 
                      ? `$${team.team_rate}/hr (Team)` 
                      : 'Individual Rates'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Visibility</span>
                  <span className="font-bold text-gray-900 flex items-center gap-1">
                    {team.visibility === 'public' ? (
                      <><Eye size={16} /> Public</>
                    ) : (
                      <><EyeSlash size={16} /> Private</>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Availability</span>
                  <span className="font-bold text-gray-900">{team.availability || 'Not set'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Completed Jobs</span>
                  <span className="font-bold text-gray-900">{team.completed_jobs || 0}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            {isMember && (
              <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
                <h3 className="font-black text-gray-900 mb-4">Actions</h3>
                <div className="space-y-3">
                  {isLeader ? (
                    <>
                      <Button
                        onClick={() => setShowInviteModal(true)}
                        className="w-full bg-[#2B4C6F]"
                      >
                        <UserPlus size={18} weight="bold" className="mr-2" />
                        Invite Workers
                      </Button>
                      <Button
                        onClick={handleDeleteTeam}
                        disabled={deletingTeam}
                        variant="outline"
                        className="w-full border-2 border-red-300 text-red-600 hover:bg-red-50"
                      >
                        {deletingTeam ? (
                          <SpinnerGap size={18} className="animate-spin mr-2" />
                        ) : (
                          <Trash size={18} weight="bold" className="mr-2" />
                        )}
                        Delete Team
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={handleLeaveTeam}
                      disabled={leavingTeam}
                      variant="outline"
                      className="w-full border-2 border-red-300 text-red-600 hover:bg-red-50"
                    >
                      {leavingTeam ? (
                        <SpinnerGap size={18} className="animate-spin mr-2" />
                      ) : (
                        <SignOut size={18} weight="bold" className="mr-2" />
                      )}
                      Leave Team
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Leader Info */}
            {team.leader && (
              <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-6">
                <h3 className="font-black text-amber-800 mb-3 flex items-center gap-2">
                  <Crown size={20} weight="fill" className="text-amber-500" />
                  Team Leader
                </h3>
                <p className="font-bold text-gray-900">{team.leader.name}</p>
                <p className="text-sm text-gray-500">{team.leader.email}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border-2 border-black shadow-[6px_6px_0px_#000] w-full max-w-lg max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-black text-gray-900">Invite Workers</h2>
                <button onClick={() => setShowInviteModal(false)} className="text-gray-500 hover:text-gray-700">
                  <X size={24} weight="bold" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                <div>
                  <Label className="font-bold mb-2 block">Search Workers</Label>
                  <div className="flex gap-2">
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by name or skill..."
                      className="h-12 border-2 border-gray-300"
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchWorkers()}
                    />
                    <Button onClick={handleSearchWorkers} disabled={searchLoading} className="h-12 px-4 bg-[#2B4C6F]">
                      {searchLoading ? <SpinnerGap size={20} className="animate-spin" /> : <MagnifyingGlass size={20} weight="bold" />}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="font-bold mb-2 block">Role (Optional)</Label>
                  <Input
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    placeholder="e.g., Electrician, Helper"
                    className="h-12 border-2 border-gray-300"
                  />
                </div>

                {searchResults.length > 0 && (
                  <div className="space-y-3">
                    <Label className="font-bold">Results</Label>
                    {searchResults.map((worker) => (
                      <div key={worker.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div>
                          <p className="font-bold text-gray-900">{worker.name}</p>
                          <p className="text-sm text-gray-500">{worker.skills?.slice(0, 2).join(', ') || 'No skills listed'}</p>
                        </div>
                        <Button
                          onClick={() => handleInviteWorker(worker.id)}
                          disabled={invitingWorker === worker.id}
                          size="sm"
                          className="bg-[#2B4C6F]"
                        >
                          {invitingWorker === worker.id ? (
                            <SpinnerGap size={16} className="animate-spin" />
                          ) : (
                            <><PaperPlaneTilt size={16} weight="bold" className="mr-1" /> Invite</>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {searchResults.length === 0 && searchQuery && !searchLoading && (
                  <p className="text-center text-gray-500 py-4">No workers found. Try a different search.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerTeamDetails;
